import { Autocomplete, TextField } from '@mui/material';
import { type FC, useMemo } from 'react';

import { usePatients } from '../../api/patients.api.js';
import { type Patient } from '../../models/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';

interface PatientFilterProps {
  id: string;
  value: Patient | null;
  onChange: (patient: Patient | null) => void;
}

function patientLabel(patient: Patient): string {
  return `${patient.firstName} ${patient.lastName}`;
}

const PatientFilter: FC<PatientFilterProps> = ({ id, value, onChange }) => {
  const { data: patients = [] } = usePatients({
    includeInactive: true,
    includeCameras: false,
    includeMatched: false,
  });

  const options = useMemo(() => [...patients].sort((a, b) => {
    if (a.active !== b.active) {
      return a.active ? -1 : 1;
    }
    return patientLabel(a).localeCompare(patientLabel(b));
  }), [patients]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, selected: Patient | null) => onChange(selected)}
      id={id}
      options={options}
      sx={{ width: 240 }}
      groupBy={(option) => (option.active ? 'Active' : 'Inactive')}
      isOptionEqualToValue={(option, selected: Patient) => option.id === selected.id}
      getOptionLabel={patientLabel}
      renderInput={(params) => <TextField {...params} label="Patient" size="small" />}
      renderOption={({ key, ...props }, option, { inputValue }) => (
        <AutocompleteOptions option={patientLabel(option)} value={inputValue} key={key} {...props} />
      )}
    />
  );
};

export default PatientFilter;
